import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, PhoneCall } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course_interest: '',
    city: '',
    program: '',
    message: ''
  });

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/centers`);
        setCenters(response.data);
      } catch (error) {
        console.error('Error fetching centers:', error);
      }
    };
    fetchCenters();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        course_interest: formData.course_interest,
        message: `City: ${formData.city}\nProgram Selected: ${formData.program}\n\nMessage: ${formData.message}`
      };
      await axios.post(`${API_URL}/api/inquiries`, payload);
      toast.success('Your inquiry has been submitted! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', course_interest: '', city: '', program: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Contact Us</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900 leading-tight">
              Have Questions? <span className="text-primary">We're Here to Help</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Connect with our academic counselors for personalized guidance on courses, admissions, and your academic journey.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}>
                Book Free Counseling
              </Button>
              <Button size="lg" variant="outline" onClick={() => toast.success('Admission details will be sent to you shortly. Please contact us at +91 9595253778.')}>
                Get Admission Details
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Phone, title: 'Call Us', info: '+91 9595253778', href: 'tel:+919595253778', sub: 'Mon-Sat, 9AM-6PM' },
              { icon: Phone, title: 'Call Us', info: '+91 9527225500', href: 'tel:+919527225500', sub: 'Mon-Sat, 9AM-6PM' },
              { icon: Mail, title: 'Email Us', info: 'info@calibernova.in', href: 'mailto:info@calibernova.in', sub: 'Response within 24hrs' },
              { icon: MapPin, title: 'Visit Us', info: '2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032', href: null, sub: 'Main Campus' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center card-hover h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <item.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold mb-1">{item.title}</h3>
                    {item.href ? (
                      <a href={item.href} className="text-primary font-medium hover:underline">{item.info}</a>
                    ) : (
                      <p className="text-primary font-medium">{item.info}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{item.sub}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="mb-8 space-y-4">
                <h3 className="font-heading text-2xl font-bold text-slate-900">We're Here to Help</h3>
                <p className="text-muted-foreground leading-relaxed">
                  At Caliber Nova, we believe that every student's journey is unique.
                  Our academic counselors are available to guide you through course selection, admission procedures, and program details to help you find the best path for your future.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Whether you're looking for Foundation Coaching, 11th & 12th Programs, Competitive Exam Preparation, or Professional Courses, we're just a call or message away.
                </p>
              </div>

              <Card className="shadow-xl animate-fade-in" id="contact-form" data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Contact Us Form</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    For admissions, course inquiries, and academic guidance, fill out the form below. Our team will get in touch with you and help you choose the right learning path for your goals.
                  </p>
                </CardHeader>
                <CardContent>
                  <h4 className="font-heading font-semibold text-base text-primary mb-4">Get Personalized Guidance</h4>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Mobile Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="Your phone number"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          data-testid="contact-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="course">Class / Course Interested In</Label>
                        <Input
                          id="course"
                          placeholder="e.g., 10th Grade / JEE Main"
                          value={formData.course_interest}
                          onChange={(e) => setFormData({ ...formData, course_interest: e.target.value })}
                          required
                          data-testid="contact-course"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          placeholder="Your city"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="program">Select Program</Label>
                        <Select value={formData.program} onValueChange={(v) => setFormData({ ...formData, program: v })}>
                          <SelectTrigger id="program">
                            <SelectValue placeholder="Choose Program" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Foundation Coaching">Foundation Coaching (7th to 10th)</SelectItem>
                            <SelectItem value="Higher Secondary Coaching">11th & 12th Science & Commerce</SelectItem>
                            <SelectItem value="Competitive Exam Prep">Entrance/Competitive Exam Prep</SelectItem>
                            <SelectItem value="Professional Courses">Professional Courses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Your Message / Query</Label>
                      <Textarea
                        id="message"
                        placeholder="How can we help you?"
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        data-testid="contact-message"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={loading} data-testid="contact-submit">
                      {loading ? 'Submitting...' : 'Submit Enquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Map & Centers */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Map Placeholder */}
              <div className="rounded-xl overflow-hidden h-64 bg-slate-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d476315.147450243!2d78.53571747343751!3d21.143956299999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c70d4fcfc3f1%3A0x22f0f5bcec44744a!2sKota%20Guru%20-%20Best%20Institute%20For%20JEE%20(Main%20%2B%20Advanced)%2C%20NEET%2C%20MHT-CET%2C%208th%2C%209th%2C%20and%2010th!5e0!3m2!1sen!2sin!4v1772896085508!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Caliber Nova Location"
                ></iframe>
              </div>

              {/* Centers List */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-xl">Our Centers</h3>
                {centers.length > 0 ? centers.slice(0, 3).map((center) => (
                  <Card key={center.id} data-testid={`center-card-${center.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <MapPin className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            {center.name}
                            {center.is_main && <Badge variant="secondary">Main</Badge>}
                          </h4>
                          <p className="text-sm text-muted-foreground">{center.address}, {center.city}</p>
                          {center.phone && <a href={`tel:${center.phone.replace(/\s+/g, '')}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">{center.phone}</a>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <MapPin className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold">Kota Main Campus</h4>
                          <p className="text-sm text-muted-foreground">Kota, Rajasthan, India</p>
                          <p className="text-sm text-muted-foreground">+91 9876543210</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 mb-8">
            Book a free counseling session and let our experts guide you to the right course.
          </p>
          <Button size="lg" variant="secondary" data-testid="cta-demo">
            Book Free Demo Class
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
